import { useCallback } from 'react';

import { useNonRevalidatingFetcher } from '~/hooks/useNonRevalidatingFetcher';
import type { CreateGroupSubscriptionSeatAdditionActionData } from '~/routes/__actions/create_group_subscription_seat_addition';

/**
 * Seat addition returns `{ message }` on success / `{ errors }` on 422, so the
 * FE only needs `ok` + a flattened `error`. There's no row payload — the seats
 * + invitations land asynchronously after the Stripe payment clears (BEE-19902),
 * so the UI just confirms the purchase was initiated and refetches.
 */
export type SeatAdditionResult = {
  ok: boolean;
  error: string | null;
};

export type UseSeatAdditionResult = {
  /** POST — buys `extraSeats` and stores `csv` so Rails can invite the queued
   *  emails once the Stripe payment clears (BEE-19902). */
  addSeats: (groupId: string, extraSeats: number, csv: Blob) => Promise<SeatAdditionResult>;
};

export function useSeatAddition(): UseSeatAdditionResult {
  const fetcher = useNonRevalidatingFetcher<() => Promise<CreateGroupSubscriptionSeatAdditionActionData>>();

  const addSeats = useCallback(
    async (groupId: string, extraSeats: number, csv: Blob): Promise<SeatAdditionResult> => {
      const formData = new FormData();
      formData.append('group_subscription_id', groupId);
      formData.append('additional_seats', String(extraSeats));
      formData.append('csv', csv, 'invite.csv');

      const submitResult = await fetcher.submit(formData, {
        method: 'post',
        action: '/create_group_subscription_seat_addition',
        encType: 'multipart/form-data',
      });
      const error = submitResult.data?.error ?? (submitResult.ok ? null : 'Failed to add seats');
      return { ok: submitResult.ok && !error, error };
    },
    [fetcher],
  );

  return { addSeats };
}
